import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormStepProps } from '../../types/application';

const StepContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const StepTitle = styled.h2`
  color: ${props => props.theme.primaryColor};
  font-size: 1.4rem;
  margin-bottom: 8px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StepDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
  }
`;

const ReviewGrid = styled.div`
  display: grid;
  gap: 15px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
    gap: 30px;
    margin-bottom: 30px;
  }
`;

const ReviewSection = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  
  @media (min-width: 768px) {
    padding: 20px;
    margin-bottom: 20px;
  }
  
  h3 {
    color: ${props => props.theme.primaryColor};
    margin: 0 0 12px 0;
    font-size: 1rem;
    font-weight: 600;
    
    @media (min-width: 768px) {
      margin: 0 0 15px 0;
      font-size: 1.1rem;
    }
  }
`;

const ReviewItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  
  .label {
    font-weight: 600;
    color: #555;
  }
  
  .value {
    color: #333;
    word-break: break-word;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TermsSection = styled.div`
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  
  @media (min-width: 768px) {
    padding: 25px;
    margin: 30px 0;
  }
`;

const TermsTitle = styled.h3`
  color: ${props => props.theme.primaryColor};
  margin: 0 0 20px 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const TermsItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  cursor: pointer;
  line-height: 1.5;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.primaryColor};
    background-color: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  input[type="checkbox"] {
    margin-top: 2px;
    transform: scale(1.2);
    accent-color: ${props => props.theme.primaryColor};
    cursor: pointer;
  }
  
  .terms-text {
    font-size: 14px;
    color: #333;
    cursor: pointer;
    
    a {
      color: ${props => props.theme.primaryColor};
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background: #ffebee;
  border-radius: 4px;
`;

const Summary = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.primaryColor} 0%, ${props => props.theme.primaryColor}dd 100%);
  color: white;
  padding: 15px;
  border-radius: 8px;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 1rem;
    
    @media (min-width: 768px) {
      margin: 0 0 15px 0;
      font-size: 1.1rem;
    }
  }
  
  .summary-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 13px;
    
    @media (min-width: 768px) {
      margin-bottom: 8px;
      font-size: 14px;
    }
    
    &:last-child {
      margin-bottom: 0;
      padding-top: 8px;
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      font-weight: 600;
      
      @media (min-width: 768px) {
        padding-top: 10px;
      }
    }
  }
`;

const NextSteps = styled.div`
  background: #e8f5e8;
  border: 1px solid #4caf50;
  border-radius: 8px;
  padding: 15px;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
  
  h4 {
    color: #2e7d32;
    margin: 0 0 12px 0;
    font-size: 1rem;
    
    @media (min-width: 768px) {
      margin: 0 0 15px 0;
      font-size: 1.1rem;
    }
  }
  
  .steps-list {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: 4px 0;
      color: #2e7d32;
      font-size: 13px;
      position: relative;
      padding-left: 18px;
      line-height: 1.4;
      
      @media (min-width: 768px) {
        padding: 5px 0;
        font-size: 14px;
        padding-left: 20px;
      }
      
      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #4caf50;
        font-weight: bold;
        font-size: 12px;
        
        @media (min-width: 768px) {
          font-size: 14px;
        }
      }
    }
  }
`;

const ReviewAndSubmit: React.FC<FormStepProps & { onValidationChange: (isValid: boolean) => void }> = ({
  data,
  updateData,
  onValidationChange,
  brand
}) => {
  const [termsAccepted, setTermsAccepted] = useState(data.termsAccepted);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleTermsChange = (field: keyof typeof termsAccepted, value: boolean) => {
    const newTerms = { ...termsAccepted, [field]: value };
    setTermsAccepted(newTerms);
    updateData({ termsAccepted: newTerms });
  };

  useEffect(() => {
    const allTermsAccepted = Object.values(termsAccepted).every(Boolean);
    const errors: string[] = [];

    if (!termsAccepted.legalAuthority) {
      errors.push('Legal authority confirmation is required');
    }
    if (!termsAccepted.documentProvision) {
      errors.push('Document provision confirmation is required');
    }
    if (!termsAccepted.policyAgreement) {
      errors.push('Policy agreement confirmation is required');
    }

    setValidationErrors(errors);
    onValidationChange(allTermsAccepted);
  }, [termsAccepted, onValidationChange]);

  return (
    <StepContainer>
      <StepTitle>Review & Submit Application</StepTitle>
      <StepDescription>
        Please review your application details carefully before submission. 
        Once submitted, our Global Compliance Team will review and contact you within 48 hours.
      </StepDescription>

      <ReviewGrid>
        <div>
          <ReviewSection>
            <h3>Business Information</h3>
            <ReviewItem>
              <div className="label">Business Name:</div>
              <div className="value">{data.businessName}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Trading Names:</div>
              <div className="value">{data.tradingNames || 'None specified'}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Company Number:</div>
              <div className="value">{data.companyNumber}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Business Type:</div>
              <div className="value">{data.businessType}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Business Model:</div>
              <div className="value">{data.businessModel}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Address:</div>
              <div className="value">
                {data.businessAddress.street}, {data.businessAddress.city}, {data.businessAddress.country}
              </div>
            </ReviewItem>
          </ReviewSection>

          <ReviewSection>
            <h3>Applicant Information</h3>
            <ReviewItem>
              <div className="label">Name:</div>
              <div className="value">{data.applicantFirstName} {data.applicantLastName}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Date of Birth:</div>
              <div className="value">{data.dateOfBirth}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Address:</div>
              <div className="value">
                {data.residentialAddress.street}, {data.residentialAddress.city}, {data.residentialAddress.country}
              </div>
            </ReviewItem>
          </ReviewSection>

          <ReviewSection>
            <h3>Contact & Financial</h3>
            <ReviewItem>
              <div className="label">Contact Person:</div>
              <div className="value">{data.contactName}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Business Email:</div>
              <div className="value">{data.businessEmail}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Bank Name:</div>
              <div className="value">{data.bankName}</div>
            </ReviewItem>
          </ReviewSection>

          <ReviewSection>
            <h3>Ownership Structure</h3>
            <ReviewItem>
              <div className="label">Major Owners:</div>
              <div className="value">{data.majorOwners}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Directors:</div>
              <div className="value">{data.directors || 'Same as major owners'}</div>
            </ReviewItem>
            <ReviewItem>
              <div className="label">Multicurrency Banking:</div>
              <div className="value">{data.multicurrencyBanking ? 'Yes' : 'No'}</div>
            </ReviewItem>
          </ReviewSection>
        </div>

        <div>
          <Summary>
            <h4>Application Summary</h4>
            <div className="summary-item">
              <span>Business:</span>
              <span>{data.businessName}</span>
            </div>
            <div className="summary-item">
              <span>Country:</span>
              <span>{data.businessAddress.country}</span>
            </div>
            <div className="summary-item">
              <span>Type:</span>
              <span>{data.businessType}</span>
            </div>
            <div className="summary-item">
              <span>Multicurrency:</span>
              <span>{data.multicurrencyBanking ? 'Included' : 'Not selected'}</span>
            </div>
            <div className="summary-item">
              <span>Status:</span>
              <span>Ready to Submit</span>
            </div>
          </Summary>

          <NextSteps>
            <h4>What happens next?</h4>
            <ul className="steps-list">
              <li>Initial compliance review (24-48 hours)</li>
              <li>Secure document upload links provided</li>
              <li>Global ID and document verification</li>
              <li>Jurisdiction-specific compliance checks</li>
              <li>Account setup and integration testing</li>
              <li>Go-live approval and onboarding</li>
            </ul>
          </NextSteps>
        </div>
      </ReviewGrid>

      <TermsSection>
        <TermsTitle>Terms and Conditions</TermsTitle>
        
        <TermsItem>
          <input
            type="checkbox"
            checked={termsAccepted.legalAuthority}
            onChange={(e) => handleTermsChange('legalAuthority', e.target.checked)}
          />
          <div className="terms-text">
            I confirm I am a legal representative of the applicant company and have the legal power 
            to register a FinaConnect account on behalf of the company
          </div>
        </TermsItem>

        <TermsItem>
          <input
            type="checkbox"
            checked={termsAccepted.documentProvision}
            onChange={(e) => handleTermsChange('documentProvision', e.target.checked)}
          />
          <div className="terms-text">
            I confirm all relevant directors and owners will provide their personal documents 
            and understand FinaConnect's privacy policy
          </div>
        </TermsItem>

        <TermsItem>
          <input
            type="checkbox"
            checked={termsAccepted.policyAgreement}
            onChange={(e) => handleTermsChange('policyAgreement', e.target.checked)}
          />
          <div className="terms-text">
            I confirm I have read and I agree with the following policies and terms:
            <br />
            <a href={`https://finaconnect.com/${brand?.id || 'finaconnect'}/privacy`} target="_blank" rel="noopener noreferrer">
              {brand?.name || 'FinaConnect'} Privacy Policy
            </a>, {' '}
            <a href={`https://finaconnect.com/${brand?.id || 'finaconnect'}/terms`} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>, {' '}
            <a href="https://hubfx.co/terms" target="_blank" rel="noopener noreferrer">
              Multicurrency Banking Terms
            </a>, {' '}
            <a href={`https://finaconnect.com/${brand?.id || 'finaconnect'}/compliance-policy`} target="_blank" rel="noopener noreferrer">
              Global Compliance Policy
            </a>
          </div>
        </TermsItem>

        {validationErrors.length > 0 && (
          <ErrorMessage>
            Please accept all required terms and conditions:
            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </ErrorMessage>
        )}
      </TermsSection>
    </StepContainer>
  );
};

export default ReviewAndSubmit;